import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Col, Container, Form, InputGroup, Modal, Row} from "react-bootstrap";
import styles from "../scss/Statistics.module.scss";
import {useParams, useSearchParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../app/store";
import {getStatistics, StatisticsData} from "../app/slices/statistics";
import {Stopwatch, Trophy} from "react-bootstrap-icons";
import {ADMIN_LANG} from "../settings";
import GameCard from "./admin/gameView/GameCard";
import {formatDuration} from "./admin/utils";
import SelectChart from "./admin/questionView/SelectChart";
import SelectTable from "./admin/questionView/SelectTable";
import ConnectChart from "./admin/questionView/ConnectChart";
import ConnectTable from "./admin/questionView/ConnectTable";
import OrderChart from "./admin/questionView/OrderChart";
import OrderTable from "./admin/questionView/OrderTable";
import OpenTable from "./admin/questionView/OpenTable";
import GamePreLoader from "./games/GamePreLoader";
import PoweredBy from "../images/powered-by-scope.svg";
import NumberScaleChart from "./admin/questionView/NumberScaleChart";
import NumberScaleTable from "./admin/questionView/NumberScaleTable";
import NumberScaleNpsTrendChart from "./admin/questionView/NumberScaleNpsTrendChart";
import AsyncTeamsSelect from "./admin/gameView/AsyncTeamsSelect";
import {PlainDetails} from "../types/admin";
import DatePicker from "react-datepicker";
import {format, subDays} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

type StatisticsFilterState = {
    teams: string[];
    preset: string | null;
    useDateRange: boolean;
    startDate: Date;
    endDate: Date;
}

type StatisticsRequestParams = {
    teams?: string[];
    date_start?: string;
    date_end?: string;
    preset?: string;
}

const DATE_RANGE_OPTION_NONE = 'none';
const DATE_RANGE_OPTION_CUSTOM = 'custom';

const parseDateValue = (value: string | null | undefined, fallbackDate: Date) => {
    if (!value) {
        return fallbackDate;
    }
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? fallbackDate : parsedDate;
};

const formatResolvedRangeDate = (value: string | null | undefined) => {
    if (!value) {
        return null;
    }

    const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
        return null;
    }

    const year = Number(dateMatch[1]);
    const month = Number(dateMatch[2]) - 1;
    const day = Number(dateMatch[3]);
    return format(new Date(year, month, day), 'dd.MM.yyyy');
};

const normalizeDateRange = (startDate: Date, endDate: Date) => {
    if (startDate > endDate) {
        return {
            startDate: endDate,
            endDate: startDate
        };
    }
    return {
        startDate,
        endDate
    };
};

const normalizeTeamIds = (teamIds: string[]) => {
    const uniqueIds = new Set(teamIds.map((teamId) => String(teamId)));
    return Array.from(uniqueIds).sort();
};

const parseFilterFromSearchParams = (
    searchParams: URLSearchParams,
    fallbackStartDate: Date,
    fallbackEndDate: Date
): StatisticsFilterState => {
    const teams = normalizeTeamIds(searchParams.getAll('teams'));
    const preset = (searchParams.get('preset') || '').trim().toLowerCase() || null;
    const dateStartValue = searchParams.get('date_start');
    const dateEndValue = searchParams.get('date_end');
    const useDateRange = !preset && Boolean(dateStartValue && dateEndValue);

    const startDate = parseDateValue(dateStartValue, fallbackStartDate);
    const endDate = parseDateValue(dateEndValue, fallbackEndDate);
    const normalizedDateRange = normalizeDateRange(startDate, endDate);

    return {
        teams,
        preset,
        useDateRange,
        startDate: normalizedDateRange.startDate,
        endDate: normalizedDateRange.endDate
    };
};

const buildStatisticsRequestParams = (filter: StatisticsFilterState): StatisticsRequestParams => {
    const normalizedDateRange = normalizeDateRange(filter.startDate, filter.endDate);
    const params: StatisticsRequestParams = {};

    if (filter.teams.length > 0) {
        params.teams = filter.teams;
    }

    if (filter.preset) {
        params.preset = filter.preset;
        return params;
    }

    if (filter.useDateRange) {
        params.date_start = format(normalizedDateRange.startDate, 'yyyy-MM-dd');
        params.date_end = format(normalizedDateRange.endDate, 'yyyy-MM-dd');
    }
    return params;
};

const buildSearchParamsFromFilter = (filter: StatisticsFilterState) => {
    const newSearchParams = new URLSearchParams();

    filter.teams.forEach((teamId) => {
        newSearchParams.append('teams', teamId);
    });

    if (filter.preset) {
        newSearchParams.set('preset', filter.preset);
        return newSearchParams;
    }

    if (filter.useDateRange) {
        const normalizedDateRange = normalizeDateRange(filter.startDate, filter.endDate);
        newSearchParams.set('date_start', format(normalizedDateRange.startDate, 'yyyy-MM-dd'));
        newSearchParams.set('date_end', format(normalizedDateRange.endDate, 'yyyy-MM-dd'));
    }
    return newSearchParams;
};

const areTeamSelectionsEqual = (teamsA: string[], teamsB: string[]) => {
    if (teamsA.length !== teamsB.length) {
        return false;
    }
    return teamsA.every((teamId, index) => teamId === teamsB[index]);
};

const areFiltersEqual = (filterA: StatisticsFilterState, filterB: StatisticsFilterState) => {
    if (!areTeamSelectionsEqual(filterA.teams, filterB.teams)) {
        return false;
    }

    if (filterA.preset !== filterB.preset) {
        return false;
    }

    if (filterA.useDateRange !== filterB.useDateRange) {
        return false;
    }

    if (filterA.preset && filterB.preset) {
        return true;
    }

    if (!filterA.useDateRange && !filterB.useDateRange) {
        return true;
    }

    return format(filterA.startDate, 'yyyy-MM-dd') === format(filterB.startDate, 'yyyy-MM-dd')
        && format(filterA.endDate, 'yyyy-MM-dd') === format(filterB.endDate, 'yyyy-MM-dd');
};

const arePlainDetailsEqual = (valuesA: PlainDetails[], valuesB: PlainDetails[]) => {
    if (valuesA.length !== valuesB.length) {
        return false;
    }

    return valuesA.every((value, index) => {
        const compareValue = valuesB[index];
        return value.value === compareValue.value && value.label === compareValue.label;
    });
};

const Statistics = () => {
    const {
        error, sections, questions,
        slides, pages, played,
        average_points, max_points, average_duration,
        statistics, name, statistics_pdf_url, theme, team_options, selected_teams,
        loaded, date_start, date_end, preset, time_presets
    } = useAppSelector((state) => state.statistics);

    const {slug} = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();

    const today = useMemo(() => new Date(), []);
    const defaultStartDate = useMemo(() => subDays(today, 30), [today]);

    const initialFilterRef = useRef<StatisticsFilterState | null>(null);
    if (!initialFilterRef.current) {
        initialFilterRef.current = parseFilterFromSearchParams(searchParams, defaultStartDate, today);
    }
    const initialFilter = initialFilterRef.current as StatisticsFilterState;

    const [appliedFilter, setAppliedFilter] = useState<StatisticsFilterState>(initialFilter);
    const [pendingFilter, setPendingFilter] = useState<StatisticsFilterState>(initialFilter);
    const [pendingSelectedTeams, setPendingSelectedTeams] = useState<PlainDetails[]>(
        initialFilter.teams.map((teamId) => ({
            value: teamId,
            label: teamId
        }))
    );
    const [showFiltersModal, setShowFiltersModal] = useState(false);

    const initialFetchSlugRef = useRef<string | null>(null);
    useEffect(() => {
        if (!slug) {
            return;
        }
        if (initialFetchSlugRef.current === slug) {
            return;
        }

        initialFetchSlugRef.current = slug;
        dispatch(getStatistics({slug, ...buildStatisticsRequestParams(initialFilter)}));
    }, [dispatch, slug, initialFilter]);

    useEffect(() => {
        if (!loaded) {
            return;
        }

        const resolvedDateRange = normalizeDateRange(
            parseDateValue(date_start, defaultStartDate),
            parseDateValue(date_end, today)
        );
        const hasResolvedDateRange = Boolean(date_start && date_end);
        const resolvedPreset = preset || null;

        const resolvedFilter: StatisticsFilterState = {
            teams: normalizeTeamIds(selected_teams.map((team) => String(team.value))),
            preset: resolvedPreset,
            useDateRange: !resolvedPreset && hasResolvedDateRange,
            startDate: resolvedDateRange.startDate,
            endDate: resolvedDateRange.endDate
        };

        setAppliedFilter((currentFilter) => areFiltersEqual(currentFilter, resolvedFilter) ? currentFilter : resolvedFilter);
        setPendingFilter((currentFilter) => areFiltersEqual(currentFilter, resolvedFilter) ? currentFilter : resolvedFilter);
        setPendingSelectedTeams((currentTeams) => arePlainDetailsEqual(currentTeams, selected_teams) ? currentTeams : selected_teams);
    }, [loaded, date_start, date_end, preset, selected_teams, defaultStartDate, today]);

    const presetOptions = useMemo(() => time_presets || [], [time_presets]);

    const teamLabelMap = useMemo(() => {
        const labelMap = new Map<string, string>();

        team_options.forEach((team) => {
            labelMap.set(String(team.value), team.label);
        });

        selected_teams.forEach((team) => {
            labelMap.set(String(team.value), team.label);
        });

        return labelMap;
    }, [team_options, selected_teams]);

    const appliedFilterSummary = useMemo(() => {
        let timeSummary = '';

        if (appliedFilter.preset || appliedFilter.useDateRange) {
            const resolvedStartDateLabel = formatResolvedRangeDate(date_start) || format(appliedFilter.startDate, 'dd.MM.yyyy');
            const resolvedEndDateLabel = formatResolvedRangeDate(date_end) || format(appliedFilter.endDate, 'dd.MM.yyyy');

            timeSummary = `${resolvedStartDateLabel} - ${resolvedEndDateLabel}`;
        }

        const parts: string[] = [];
        if (timeSummary) {
            parts.push(`${ADMIN_LANG.date_range}: ${timeSummary}`);
        }

        if (appliedFilter.teams.length) {
            const appliedTeamLabels = appliedFilter.teams.map((teamId) => teamLabelMap.get(teamId) || teamId);
            parts.push(`${ADMIN_LANG.teams}: ${appliedTeamLabels.join(', ')}`);
        }

        if (!parts.length) {
            return ADMIN_LANG.all || 'All';
        }

        return parts.join(' | ');
    }, [appliedFilter, date_start, date_end, teamLabelMap]);

    const hasPendingChanges = useMemo(
        () => !areFiltersEqual(pendingFilter, appliedFilter),
        [pendingFilter, appliedFilter]
    );
    const hasActiveFilters = useMemo(
        () => appliedFilter.teams.length > 0 || Boolean(appliedFilter.preset) || appliedFilter.useDateRange,
        [appliedFilter]
    );
    const isCustomDateRangeSelected = !pendingFilter.preset && pendingFilter.useDateRange;
    const presetSelectValue = pendingFilter.preset
        || (isCustomDateRangeSelected ? DATE_RANGE_OPTION_CUSTOM : DATE_RANGE_OPTION_NONE);

    const handleTeamsChange = useCallback((selectedValues: PlainDetails[]) => {
        const nextSelectedTeams = selectedValues || [];

        setPendingSelectedTeams(nextSelectedTeams);
        setPendingFilter((currentFilter) => ({
            ...currentFilter,
            teams: normalizeTeamIds(nextSelectedTeams.map((team) => String(team.value)))
        }));
    }, []);

    const handlePresetChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextValue = event.target.value;

        setPendingFilter((currentFilter) => ({
            ...currentFilter,
            preset: nextValue === DATE_RANGE_OPTION_NONE || nextValue === DATE_RANGE_OPTION_CUSTOM ? null : nextValue,
            useDateRange: nextValue === DATE_RANGE_OPTION_CUSTOM
        }));
    }, []);

    const handleStartDateChange = useCallback((date: Date | null) => {
        if (!date) {
            return;
        }

        setPendingFilter((currentFilter) => {
            const normalizedRange = normalizeDateRange(date, currentFilter.endDate);
            return {
                ...currentFilter,
                preset: null,
                useDateRange: true,
                startDate: normalizedRange.startDate,
                endDate: normalizedRange.endDate
            };
        });
    }, []);

    const handleEndDateChange = useCallback((date: Date | null) => {
        if (!date) {
            return;
        }

        setPendingFilter((currentFilter) => {
            const normalizedRange = normalizeDateRange(currentFilter.startDate, date);
            return {
                ...currentFilter,
                preset: null,
                useDateRange: true,
                startDate: normalizedRange.startDate,
                endDate: normalizedRange.endDate
            };
        });
    }, []);

    const handleApplyFilters = useCallback(() => {
        if (!slug) {
            return;
        }

        const normalizedFilter: StatisticsFilterState = {
            ...pendingFilter,
            teams: normalizeTeamIds(pendingFilter.teams),
            ...normalizeDateRange(pendingFilter.startDate, pendingFilter.endDate),
            useDateRange: pendingFilter.preset ? false : pendingFilter.useDateRange
        };

        if (areFiltersEqual(normalizedFilter, appliedFilter)) {
            setShowFiltersModal(false);
            return;
        }

        setAppliedFilter(normalizedFilter);
        setPendingFilter(normalizedFilter);
        setSearchParams(buildSearchParamsFromFilter(normalizedFilter));
        dispatch(getStatistics({slug, ...buildStatisticsRequestParams(normalizedFilter)}));
        setShowFiltersModal(false);
    }, [slug, pendingFilter, appliedFilter, setSearchParams, dispatch]);

    const handleRevertPendingChanges = useCallback(() => {
        setPendingFilter(appliedFilter);
        setPendingSelectedTeams(selected_teams);
    }, [appliedFilter, selected_teams]);
    const handleResetFilters = useCallback(() => {
        setPendingFilter({
            teams: [],
            preset: null,
            useDateRange: false,
            startDate: defaultStartDate,
            endDate: today
        });
        setPendingSelectedTeams([]);
    }, [defaultStartDate, today]);

    const handleOpenFiltersModal = useCallback(() => {
        setShowFiltersModal(true);
    }, []);

    const handleCancelFiltersModal = useCallback(() => {
        handleRevertPendingChanges();
        setShowFiltersModal(false);
    }, [handleRevertPendingChanges]);

    const handleOpenPdfUrl = useCallback(() => {
        if (statistics_pdf_url) {
            window.open(statistics_pdf_url, '_blank');
        }
    }, [statistics_pdf_url]);

    const renderStatisticsSection = useCallback((stat: StatisticsData) => {
        switch (stat.type) {
            case "select":
                return (
                    <>
                        <SelectChart optionsData={stat.select_data}/>
                        <SelectTable maxPoints={max_points} optionsData={stat.select_data}/>
                    </>
                );
            case "connect":
                return (
                    <>
                        <ConnectChart optionsData={stat.connect_data}/>
                        <ConnectTable optionsData={stat.connect_data}/>
                    </>
                );
            case "order":
                return (
                    <>
                        <OrderChart optionsData={stat.order_data}/>
                        <OrderTable optionsData={stat.order_data}/>
                    </>
                );
            case "text":
                return (
                    <>
                        <OpenTable optionsData={stat.text_data}/>
                    </>
                );
            case "number_scale":
                return (
                    <>
                        <NumberScaleChart optionsData={stat.number_scale_data} npsScale={stat.nps_data}/>
                        <NumberScaleTable optionsData={stat.number_scale_data} npsScale={stat.nps_data}/>
                        <NumberScaleNpsTrendChart
                            npsScale={stat.nps_data}
                            dateStart={date_start || undefined}
                            dateEnd={date_end || undefined}
                        />
                    </>
                );
            case "content":
                return null;
            default:
                return null;
        }
    }, [max_points, date_start, date_end]);

    const renderPageBreak = useCallback((stat: StatisticsData) => {
        switch (stat.type) {
            case "select":
            case "connect":
            case "order":
            case "text":
            case "number_scale":
                return <div className={'print-break-before'}/>;
            default:
                return null;
        }
    }, []);

    const statisticsRows = useMemo(() => statistics?.map((stat, index) => {
        return (
            <Row key={`stat-section-${index}`} className="pb-5 gy-3 print-avoid-break">
                <Col xs={12}>
                    {renderPageBreak(stat)}
                    <h2><span className="badge bg-white text-dark me-1">#{index + 1}</span> {stat.title} <span
                        className="ms-2 badge bg-info">{stat.type_display}</span></h2>
                    <div className={styles.contentInfo}>
                        {stat.played !== null && (
                            <p>{ADMIN_LANG.played}: {stat.played} {ADMIN_LANG.times}</p>
                        )}
                        {stat.average_points !== null && stat.max_points !== null && stat.max_points > 0 && (
                            <p>{ADMIN_LANG.average_points}: {stat.average_points} / {stat.max_points}</p>
                        )}
                        <p>{ADMIN_LANG.average_duration}: {formatDuration(stat.average_duration)}</p>
                        {stat.type == "number_scale" && typeof stat.average_rating !== "undefined" && stat.average_rating !== null && (
                            <p>{ADMIN_LANG.average}: {stat.average_rating}</p>
                        )}
                    </div>
                </Col>
                {renderStatisticsSection(stat)}
            </Row>
        );
    }), [statistics, renderPageBreak, renderStatisticsSection]);

    return (
        <>
        {theme && (
            <div className={styles.topBarStatistics} style={{ backgroundColor: theme.colors.top_bar_background }}>
                <div className={styles.topBarStatisticsContainer}>
                    <img src={theme.logo} alt='Logo' />
                </div>
            </div>
        )}
        <Container className={`${styles.containerStatistic} admin`}>
            {error && <div className="alert alert-danger">{error}</div>}

            <div>
                <img className={`${styles.poweredByStatistics}`} src={PoweredBy} alt='Powered by Scope'/>
            </div>

            <div className='d-flex justify-content-between align-items-center pb-3'>
                <h1>{name}</h1>
                <div className='d-flex align-items-end'>
                    <Button className={styles.hideForPrint} onClick={handleOpenPdfUrl} variant='primary'>{ADMIN_LANG.export_pdf}</Button>
                    <Button className={`${styles.hideForPrint} ms-2`} variant='outline-secondary' onClick={handleOpenFiltersModal}>
                        {ADMIN_LANG.filter}
                    </Button>
                </div>
            </div>
            {hasActiveFilters && (
                <Form.Text className={`text-muted ${styles.appliedFilterSummary}`}>
                    {`${(ADMIN_LANG as {[key: string]: string}).filtered_view} - ${appliedFilterSummary}`}
                </Form.Text>
            )}

            <Modal show={showFiltersModal} onHide={handleCancelFiltersModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{ADMIN_LANG.filter}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {team_options.length > 0 && (
                        <Form.Group controlId='teams' className={styles.filterModalSection}>
                            <Form.Label>{ADMIN_LANG.select_teams}</Form.Label>
                            <AsyncTeamsSelect selectedTeams={pendingSelectedTeams} initialValues={team_options} onChange={handleTeamsChange}/>
                        </Form.Group>
                    )}

                    <Form.Group controlId='preset' className={styles.filterModalSection}>
                        <Form.Label>{ADMIN_LANG.date_range}</Form.Label>
                        <Form.Select value={presetSelectValue} onChange={handlePresetChange} className={styles.presetSelect}>
                            <option value={DATE_RANGE_OPTION_NONE}>{(ADMIN_LANG as {[key: string]: string}).no_time_limit || 'Ei aikarajausta'}</option>
                            <option value={DATE_RANGE_OPTION_CUSTOM}>{(ADMIN_LANG as {[key: string]: string}).custom_range || 'Itse määritelty aikarajaus'}</option>
                            {presetOptions.map((option) => (
                                <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group controlId='dateRange' className={styles.filterModalSection}>
                        <div className={styles.dateRangeRow}>
                            <Form.Group className={styles.dateFieldGroup}>
                                <Form.Label>{ADMIN_LANG.start_date}</Form.Label>
                                <InputGroup>
                                    <DatePicker
                                        selected={pendingFilter.startDate}
                                        onChange={handleStartDateChange}
                                        startDate={pendingFilter.startDate}
                                        endDate={pendingFilter.endDate}
                                        maxDate={today}
                                        dateFormat="dd.MM.yyyy"
                                        selectsStart
                                        className='date-picker form-control'
                                        disabled={!isCustomDateRangeSelected}
                                    />
                                </InputGroup>
                            </Form.Group>
                            <Form.Group className={`${styles.dateFieldGroup} ms-2`}>
                                <Form.Label>{ADMIN_LANG.end_date}</Form.Label>
                                <InputGroup>
                                    <DatePicker
                                        selected={pendingFilter.endDate}
                                        onChange={handleEndDateChange}
                                        startDate={pendingFilter.startDate}
                                        endDate={pendingFilter.endDate}
                                        maxDate={today}
                                        minDate={pendingFilter.startDate}
                                        dateFormat="dd.MM.yyyy"
                                        selectsEnd
                                        className='date-picker form-control'
                                        disabled={!isCustomDateRangeSelected}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='outline-secondary'
                        onClick={handleCancelFiltersModal}
                    >
                        {ADMIN_LANG.cancel}
                    </Button>
                    <Button
                        variant='outline-secondary'
                        onClick={handleResetFilters}
                        disabled={(!hasPendingChanges && !hasActiveFilters) || !loaded}
                    >
                        {ADMIN_LANG.reset}
                    </Button>
                    <Button
                        onClick={handleApplyFilters}
                        disabled={!loaded}
                    >
                        {ADMIN_LANG.apply}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Row className='pb-5'>
                <GameCard
                    icon={Trophy}
                    items={[
                        {label: ADMIN_LANG.sections, value: sections},
                        {label: ADMIN_LANG.questions, value: questions},
                        {label: ADMIN_LANG.slides, value: slides},
                        {label: ADMIN_LANG.pages, value: pages}
                    ]}
                    colProps={{xs: 12, lg: 6}}
                />
                <GameCard
                    icon={Stopwatch}
                    items={[
                        {label: ADMIN_LANG.games_played, value: played},
                        {label: ADMIN_LANG.average_points, value: average_points},
                        {label: ADMIN_LANG.max_points, value: max_points},
                        {label: ADMIN_LANG.average_duration, value: formatDuration(average_duration)}
                    ]}
                    colProps={{xs: 12, lg: 6}}
                />
            </Row>

            {statisticsRows}
        <GamePreLoader isThemeLoaded={loaded}/>
        </Container>
        </>
    )
}

export default Statistics;
